import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET_DESKTOP_LAYOUT, SAVE_DESKTOP_LAYOUT } from "../../Queries/desktopLayout";

/**
 * desktopLayoutSlice — the per-user Webtop layout (Phase 7 persistence).
 *
 * Holds the layout object locally and syncs it to the backend DesktopLayout
 * model (keyed server-side to the JWT identity). Phases 8–10 read/write the
 * `layout` object (icons, folders, taskbar pins, record shortcuts, sticky
 * notes); this slice just persists it.
 *
 * The Apollo client is imported lazily inside the thunks (not at module top) so
 * importing this slice in tests does NOT execute src/index.js (which renders the
 * whole app on import).
 */

export const fetchDesktopLayoutAsync = createAsyncThunk(
  "desktopLayout/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const { client } = await import("../../index");
      const { data } = await client.query({
        query: GET_DESKTOP_LAYOUT,
        fetchPolicy: "no-cache",
      });
      const raw = data?.getDesktopLayout?.data;
      const parsed = raw ? JSON.parse(raw) : {};
      // Guard against corrupted/non-object payloads → fall back to an empty layout.
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (err) {
      return rejectWithValue(err.message || "Failed to load desktop layout");
    }
  }
);

export const saveDesktopLayoutAsync = createAsyncThunk(
  "desktopLayout/save",
  async (layout, { rejectWithValue }) => {
    try {
      const { client } = await import("../../index");
      await client.mutate({
        mutation: SAVE_DESKTOP_LAYOUT,
        variables: { data: JSON.stringify(layout || {}) },
      });
      return layout || {};
    } catch (err) {
      return rejectWithValue(err.message || "Failed to save desktop layout");
    }
  }
);

const initialState = {
  layout: {},
  loaded: false,
  loading: false,
  saving: false,
  error: null,
  // Last autosave failure (e.g. backend rejected an oversized layout). Surfaced
  // to the user so persistence never fails silently; cleared on the next save.
  saveError: null,
};

const desktopLayoutSlice = createSlice({
  name: "desktopLayout",
  initialState,
  reducers: {
    // Replace the whole layout (local only — caller persists separately).
    setLayout(state, action) {
      state.layout = action.payload || {};
    },
    // Shallow-merge a partial update into the layout.
    patchLayout(state, action) {
      state.layout = { ...state.layout, ...(action.payload || {}) };
    },
    clearSaveError(state) {
      state.saveError = null;
    },
    resetLayout(state) {
      state.layout = {};
      state.loaded = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDesktopLayoutAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDesktopLayoutAsync.fulfilled, (state, action) => {
        state.layout = action.payload || {};
        state.loaded = true;
        state.loading = false;
      })
      .addCase(fetchDesktopLayoutAsync.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true; // don't block the UI on a load failure
        state.error = action.payload;
      })
      .addCase(saveDesktopLayoutAsync.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(saveDesktopLayoutAsync.fulfilled, (state) => {
        // Do NOT reassign state.layout here. The save thunk carries a snapshot
        // taken when the debounce was scheduled; the live local layout is already
        // the source of truth and may be newer. Reassigning could revert a change
        // made while the save was in flight (and lose it if a later save fails).
        state.saving = false;
        state.saveError = null;
      })
      .addCase(saveDesktopLayoutAsync.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
        state.saveError = action.payload;
      });
  },
});

export const { setLayout, patchLayout, clearSaveError, resetLayout } = desktopLayoutSlice.actions;

export const selectLayout = (state) => state.desktopLayout?.layout || {};
export const selectLayoutLoaded = (state) => state.desktopLayout?.loaded || false;
export const selectLayoutSaveError = (state) => state.desktopLayout?.saveError || null;

export default desktopLayoutSlice.reducer;
